import React, { useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAlertToast } from '../store/alertToastStore';
export const AlertToast = memo(function AlertToast() {
  const {
    queue,
    dismissToast
  } = useAlertToast();
  const navigate = useNavigate();
  const currentAlert = queue[0];
  useEffect(() => {
    if (currentAlert) {
      const timer = setTimeout(() => {
        dismissToast();
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [currentAlert, dismissToast]);
  if (!currentAlert) return null;
  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'high':
      case 'critical':
        return {
          borderTop: 'border-danger',
          iconBg: 'bg-danger/15',
          iconColor: 'text-danger',
          Icon: AlertCircle
        };
      case 'medium':
        return {
          borderTop: 'border-[#F59E0B]',
          iconBg: 'bg-[#F59E0B]/15',
          iconColor: 'text-[#F59E0B]',
          Icon: AlertTriangle
        };
      case 'low':
      default:
        return {
          borderTop: 'border-success',
          iconBg: 'bg-success/15',
          iconColor: 'text-success',
          Icon: Info
        };
    }
  };
  const styles = getSeverityStyles(currentAlert.severity);
  const Icon = styles.Icon;
  const handleToastClick = (e: React.MouseEvent) => {
    // Prevent navigation if clicking the close button
    if ((e.target as HTMLElement).closest('.close-btn')) return;
    dismissToast();
    navigate('/app/alerts');
  };
  return <AnimatePresence data-id="element-122" data-mp="1evs4ylm3acae">
      <motion.div key={currentAlert.id} initial={{
      y: -120,
      x: '-50%',
      opacity: 0
    }} animate={{
      y: 0,
      x: '-50%',
      opacity: 1
    }} exit={{
      y: -120,
      x: '-50%',
      opacity: 0
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className={`fixed top-[16px] left-1/2 z-[9999] w-[calc(100%-32px)] max-w-[380px] bg-white rounded-[16px] shadow-elevated overflow-hidden cursor-pointer border-t-4 ${styles.borderTop}`} onClick={handleToastClick} data-id="element-123" data-mp="19ukz4s3ibn6d">
        <div className="p-4 flex items-start gap-3" data-id="element-124" data-mp="3jcovcrzedtov">
          <div className={`w-[36px] h-[36px] rounded-full flex items-center justify-center flex-shrink-0 ${styles.iconBg}`} data-id="element-125" data-mp="avu5cd3p83s7">
            <Icon size={20} className={styles.iconColor} data-id="element-126" data-mp="ew4bpqvekw7l" />
          </div>

          <div className="flex-1 min-w-0 pt-0.5" data-id="element-127" data-mp="y618wk7djxd">
            <h4 className="text-[14px] font-semibold text-text-primary mb-0.5" data-id="element-128" data-mp="zqt2ef4uorj">
              New Alert
            </h4>
            <p className="text-[13px] text-text-secondary truncate" data-id="element-129" data-mp="2lnvb0v61ufj7">
              {currentAlert.message}
            </p>
          </div>

          <button onClick={e => {
          e.stopPropagation();
          dismissToast();
        }} aria-label="Dismiss alert" className="close-btn w-[28px] h-[28px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors flex-shrink-0 -mt-1 -mr-1" data-id="element-130" data-mp="2qnzt2pmlvibj">
            <X size={16} className="text-text-secondary" data-id="element-131" data-mp="2s2171g46yz7r" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-[3px] w-full bg-border/50 absolute bottom-0 left-0" data-id="element-132" data-mp="1zfg55lq3b4py">
          <div className="h-full bg-primary" style={{
          animation: 'shrinkWidth 6s linear forwards'
        }} data-id="element-133" data-mp="1zddinbo5btpz" />
        </div>
        <style data-id="element-134" data-mp="296ubodggl4sl">{`
          @keyframes shrinkWidth {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
      </motion.div>
    </AnimatePresence>;
});